<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import LoginForm from './components/auth/LoginForm.vue'
import RegisterForm from './components/auth/RegisterForm.vue'
import ForgotPasswordModal from './components/auth/ForgotPasswordModal.vue'
import ResetPasswordView from './components/auth/ResetPasswordView.vue'
import ProfileModal from './components/profile/ProfileModal.vue'
import GameLobby from './components/lobby/GameLobby.vue'
import GameBoard from './components/game/GameBoard.vue'
import { Logger, LogCategory } from '@/utils/Logger.class'

// API URL configuration
const isDevPort = window.location.port && !['80', '443', ''].includes(window.location.port)
const API_URL = (window.location.hostname === 'localhost' && isDevPort)
  ? `${window.location.protocol}//localhost:8000/api`
  : `${window.location.origin}/api`

// Session storage keys for game state persistence across refresh
const SESSION_KEYS = {
  SCREEN: 'canasta_current_screen',
  ROOM_CODE: 'canasta_room_code',
  PLAYER_ID: 'canasta_player_id',
  IS_CREATOR: 'canasta_is_creator',
}

// Simple screen management (will be replaced with router later)
const currentScreen = ref<'login' | 'lobby' | 'game'>('login')
const isAuthenticated = ref(false)
const userData = ref<any>(null)
const isCheckingAuth = ref(true)
const errorMessage = ref<string | null>(null)
const showRegister = ref(false)
const showForgotPassword = ref(false)
const showProfileModal = ref(false)
const resetToken = ref<string | null>(null)

// Game state
const gameData = ref<any>(null)

/**
 * Save game session state to sessionStorage for recovery after browser refresh
 */
function saveGameSession() {
  if (currentScreen.value === 'game' && gameData.value) {
    sessionStorage.setItem(SESSION_KEYS.SCREEN, 'game')
    sessionStorage.setItem(SESSION_KEYS.ROOM_CODE, gameData.value.roomCode || '')
    sessionStorage.setItem(SESSION_KEYS.PLAYER_ID, gameData.value.playerId || '')
    // Preserve creator status - check localStorage first (set when creating game)
    // then fall back to existing sessionStorage value
    const isCreator = localStorage.getItem('canasta_is_creator') === 'true' ||
                      sessionStorage.getItem(SESSION_KEYS.IS_CREATOR) === 'true'
    sessionStorage.setItem(SESSION_KEYS.IS_CREATOR, isCreator ? 'true' : 'false')
    console.log('[App] Saved game session:', {
      screen: 'game',
      roomCode: gameData.value.roomCode,
      playerId: gameData.value.playerId,
      isCreator,
    })
  }
}

/**
 * Restore game session from sessionStorage after browser refresh
 * Returns true if session was restored successfully
 */
function restoreGameSession(): boolean {
  const savedScreen = sessionStorage.getItem(SESSION_KEYS.SCREEN)
  const savedRoomCode = sessionStorage.getItem(SESSION_KEYS.ROOM_CODE)
  const savedPlayerId = sessionStorage.getItem(SESSION_KEYS.PLAYER_ID)

  if (savedScreen === 'game' && savedRoomCode) {
    console.log('[App] Restoring game session:', { savedScreen, savedRoomCode, savedPlayerId })
    gameData.value = {
      roomCode: savedRoomCode,
      playerId: savedPlayerId || undefined,
    }
    currentScreen.value = 'game'
    return true
  }
  return false
}

/**
 * Clear game session from sessionStorage
 */
function clearGameSession() {
  sessionStorage.removeItem(SESSION_KEYS.SCREEN)
  sessionStorage.removeItem(SESSION_KEYS.ROOM_CODE)
  sessionStorage.removeItem(SESSION_KEYS.PLAYER_ID)
  sessionStorage.removeItem(SESSION_KEYS.IS_CREATOR)
  // Also clear localStorage creator flag
  localStorage.removeItem('canasta_is_creator')
  console.log('[App] Cleared game session from sessionStorage')
}

// Watch for screen changes and persist game state
watch(currentScreen, (newScreen) => {
  if (newScreen === 'game') {
    saveGameSession()
  } else if (newScreen === 'lobby') {
    // Clear game session when returning to lobby (intentional navigation)
    clearGameSession()
  }
})

// Watch gameData changes to persist updates
watch(gameData, () => {
  if (currentScreen.value === 'game') {
    saveGameSession()
  }
}, { deep: true })

/**
 * Check if user is already authenticated via stored tokens
 */
async function checkExistingAuth() {
  const accessToken = localStorage.getItem('access_token')

  if (!accessToken) {
    isCheckingAuth.value = false
    return
  }

  try {
    // Try to fetch current user with existing token
    const response = await fetch(`${API_URL}/auth/me`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    })

    if (response.ok) {
      const user = await response.json()
      // Restore authenticated state
      isAuthenticated.value = true
      userData.value = user
      // Try to restore game session first, fallback to lobby
      if (!restoreGameSession()) {
        currentScreen.value = 'lobby'
      }
    } else if (response.status === 401) {
      // Token expired, try to refresh
      await tryRefreshToken()
    } else {
      // Other error, clear tokens
      clearAuthTokens()
    }
  } catch (error) {
    console.error('Auth check failed:', error)
    clearAuthTokens()
  } finally {
    isCheckingAuth.value = false
  }
}

/**
 * Try to refresh the access token using refresh token
 */
async function tryRefreshToken() {
  const refreshToken = localStorage.getItem('refresh_token')

  if (!refreshToken) {
    clearAuthTokens()
    return
  }

  try {
    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refresh_token: refreshToken,
      }),
    })

    if (response.ok) {
      const data = await response.json()
      // Update tokens
      localStorage.setItem('access_token', data.access_token)
      localStorage.setItem('refresh_token', data.refresh_token)
      // Restore authenticated state
      isAuthenticated.value = true
      userData.value = data.user
      // Try to restore game session first, fallback to lobby
      if (!restoreGameSession()) {
        currentScreen.value = 'lobby'
      }
    } else {
      // Refresh failed, clear tokens
      clearAuthTokens()
    }
  } catch (error) {
    console.error('Token refresh failed:', error)
    clearAuthTokens()
  }
}

/**
 * Clear all authentication tokens and game session
 */
function clearAuthTokens() {
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
  clearGameSession()
  isAuthenticated.value = false
  userData.value = null
  currentScreen.value = 'login'
}

function handleLogin(user: any) {
  console.log('[App] handleLogin - user:', user)
  isAuthenticated.value = true
  userData.value = user
  currentScreen.value = 'lobby'
  showRegister.value = false
  console.log('[App] Transitioned to lobby screen')
}

function handleSwitchToRegister() {
  showRegister.value = true
}

function handleSwitchToLogin() {
  showRegister.value = false
}

function handleRegister(user: any) {
  console.log('[App] handleRegister - user:', user)
  isAuthenticated.value = true
  userData.value = user
  currentScreen.value = 'lobby'
  showRegister.value = false
}

function handleGameCreated(data: any) {
  console.log('[App] handleGameCreated - data:', data)
  // Store game data for GameBoard component
  gameData.value = data
  console.log('[App] Game data stored:', gameData.value)
}

function handleGameJoined(data: any) {
  Logger.info(LogCategory.UI, '[JOIN-7] handleGameJoined called with:', data)
  // seat_assigned provides room_code/player_id in snake_case
  // Transform to camelCase for GameBoard props
  gameData.value = {
    roomCode: data.room_code || data.roomCode,
    playerId: data.player_id || data.playerId,
    seat: data.seat,
    roomState: data.room_state || data.roomState,
    ...data
  }
  Logger.info(LogCategory.UI, '[JOIN-7] Game data stored:', gameData.value)
}

function handleGameStart() {
  Logger.info(LogCategory.UI, '[JOIN-8] handleGameStart - transitioning from', currentScreen.value, 'to game')
  Logger.info(LogCategory.UI, '[JOIN-8] Game data for GameBoard:', gameData.value)
  currentScreen.value = 'game'
  Logger.info(LogCategory.UI, '[JOIN-8] ✅ Transitioned to game screen')
}

function handleBackToLobby() {
  currentScreen.value = 'lobby'
  gameData.value = null
}

function handleError(message: string) {
  errorMessage.value = message
  // Clear error after 5 seconds
  setTimeout(() => {
    errorMessage.value = null
  }, 5000)
}

/**
 * Check URL for password reset token
 * Returns true if token was found and we should show reset view
 */
function checkResetToken(): boolean {
  const urlParams = new URLSearchParams(window.location.search)
  const token = urlParams.get('token')

  if (token) {
    console.log('[App] Password reset token detected in URL')
    resetToken.value = token
    // Clear the token from URL without reload
    window.history.replaceState({}, document.title, window.location.pathname)
    return true
  }
  return false
}

function handleForgotPassword() {
  showForgotPassword.value = true
}

function handleForgotPasswordClose() {
  showForgotPassword.value = false
}

function handleProfileOpen() {
  showProfileModal.value = true
}

function handleProfileClose() {
  showProfileModal.value = false
}

function handleResetSuccess() {
  // Clear reset token and return to login
  resetToken.value = null
  currentScreen.value = 'login'
}

function handleBackToLogin() {
  resetToken.value = null
  currentScreen.value = 'login'
}

// Check for existing authentication on app mount
onMounted(() => {
  // Check for password reset token first
  if (checkResetToken()) {
    isCheckingAuth.value = false
    return
  }

  checkExistingAuth()
})
</script>

<template>
  <div id="app">
    <!-- Password Reset View (from email link) -->
    <ResetPasswordView
      v-if="resetToken"
      :token="resetToken"
      @success="handleResetSuccess"
      @back-to-login="handleBackToLogin"
    />

    <!-- Loading Screen (checking auth) -->
    <div v-else-if="isCheckingAuth" id="auth-screen" class="screen active">
      <img src="/images/logo.png" alt="Canasta - Hooley Style" class="auth-logo">
      <div id="auth-forms" style="text-align: center;">
        <p style="color: rgba(255, 255, 255, 0.7); font-size: 1rem;">Loading...</p>
      </div>
    </div>

    <!-- Auth Screen -->
    <div v-else-if="currentScreen === 'login'" id="auth-screen" class="screen active">
      <img src="/images/logo.png" alt="Canasta - Hooley Style" class="auth-logo">
      <div id="auth-forms">
        <LoginForm
          v-if="!showRegister"
          @success="handleLogin"
          @switchToRegister="handleSwitchToRegister"
          @forgotPassword="handleForgotPassword"
        />
        <RegisterForm
          v-else
          @success="handleRegister"
          @switchToLogin="handleSwitchToLogin"
        />
      </div>
    </div>

    <!-- Lobby Screen -->
    <GameLobby
      v-else-if="currentScreen === 'lobby'"
      :username="userData?.username || userData?.email || 'Player'"
      :user-id="userData?.id || userData?.user_id || ''"
      @game-created="handleGameCreated"
      @game-joined="handleGameJoined"
      @game-start="handleGameStart"
      @error="handleError"
      @logout="clearAuthTokens"
      @profile="handleProfileOpen"
    />

    <!-- Game Screen -->
    <GameBoard
      v-else-if="currentScreen === 'game'"
      :room-code="gameData?.roomCode"
      :player-id="gameData?.playerId"
      @back-to-lobby="handleBackToLobby"
    />

    <!-- Error Toast -->
    <div v-if="errorMessage" class="error-toast">
      {{ errorMessage }}
    </div>

    <!-- Forgot Password Modal -->
    <ForgotPasswordModal
      :is-open="showForgotPassword"
      @close="handleForgotPasswordClose"
    />

    <!-- Profile Modal -->
    <ProfileModal
      v-if="showProfileModal"
      @close="handleProfileClose"
    />
  </div>
</template>

<style>
/* Global styles are imported in main.ts */
#app {
  width: 100%;
}

/* Auth Screen - Compact single-viewport design */
#auth-screen {
  height: 100vh;
  height: 100dvh; /* Dynamic viewport height for mobile */
  max-height: 100vh;
  background: linear-gradient(145deg, #0a1520 0%, #0d1f2d 30%, #142838 60%, #0d1f2d 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  position: relative;
  overflow: hidden;
  gap: 1.25rem;
}

/* Ambient glow effect */
#auth-screen::before {
  content: '';
  position: absolute;
  top: 20%;
  left: 50%;
  transform: translateX(-50%);
  width: 600px;
  height: 400px;
  background: radial-gradient(ellipse, rgba(212, 167, 44, 0.06) 0%, transparent 60%);
  pointer-events: none;
  z-index: 0;
}

/* Secondary ambient glow */
#auth-screen::after {
  content: '';
  position: absolute;
  bottom: 10%;
  left: 50%;
  transform: translateX(-50%);
  width: 500px;
  height: 300px;
  background: radial-gradient(ellipse, rgba(16, 185, 129, 0.04) 0%, transparent 60%);
  pointer-events: none;
  z-index: 0;
}

.auth-logo {
  max-width: 220px;
  width: 100%;
  height: auto;
  filter: drop-shadow(0 4px 16px rgba(212, 167, 44, 0.25));
  position: relative;
  z-index: 10;
  flex-shrink: 0;
}

#auth-forms {
  position: relative;
  max-width: 380px;
  width: 100%;
  background: linear-gradient(165deg, rgba(16, 26, 38, 0.95) 0%, rgba(12, 20, 30, 0.98) 100%);
  border-radius: 20px;
  padding: 1.75rem 1.75rem 1.5rem;
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(16px);
  animation: floatIn 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 10;
}

@keyframes floatIn {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Subtle border glow */
#auth-forms::before {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: 21px;
  padding: 1px;
  background: linear-gradient(165deg, rgba(212, 167, 44, 0.2), rgba(212, 167, 44, 0.05) 50%, rgba(16, 185, 129, 0.1));
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  opacity: 0.7;
  pointer-events: none;
}

/* Error Toast */
.error-toast {
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  padding: 1rem 2rem;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.95) 0%, rgba(220, 38, 38, 0.95) 100%);
  color: white;
  border-radius: 12px;
  box-shadow:
    0 8px 32px rgba(239, 68, 68, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
  font-size: 0.9375rem;
  font-weight: 600;
  z-index: 9999;
  animation: slideUp 0.3s ease-out;
  max-width: 90%;
  text-align: center;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

/* Responsive - Mobile optimization */
@media (max-width: 640px) {
  #auth-screen {
    padding: 1rem;
    gap: 1rem;
  }

  .auth-logo {
    max-width: 180px;
  }

  #auth-forms {
    padding: 1.5rem 1.25rem 1.25rem;
    border-radius: 16px;
  }

  #auth-forms::before {
    border-radius: 17px;
  }

  .error-toast {
    bottom: 1rem;
    padding: 0.75rem 1.25rem;
    font-size: 0.8125rem;
  }
}

/* Extra small screens */
@media (max-height: 700px) {
  #auth-screen {
    padding: 0.75rem;
    gap: 0.75rem;
  }

  .auth-logo {
    max-width: 160px;
  }

  #auth-forms {
    padding: 1.25rem 1.25rem 1rem;
  }
}
</style>
